import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'resident' | 'supervisor' | 'admin';
  specialties?: string[];
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (userData: SignupData) => Promise<void>;
  loginAsDemo: (role: 'resident' | 'supervisor' | 'admin') => Promise<void>;
  logout: () => void;
}

interface SignupData {
  name: string;
  email: string;
  password: string;
  role: 'resident' | 'supervisor' | 'admin';
  specialties?: string[];
}

// Demo users for quick testing
const DEMO_USERS: Record<string, User> = {
  resident: {
    id: 'demo-resident',
    name: 'Dr. Jane Resident',
    email: 'resident@demo.com',
    role: 'resident',
    specialties: ['Radiology'],
    permissions: ['view_clinicalItems', 'edit_reports', 'request_consultation']
  },
  supervisor: {
    id: 'demo-supervisor',
    name: 'Dr. John Supervisor',
    email: 'supervisor@demo.com',
    role: 'supervisor',
    specialties: ['Radiology', 'Nuclear Medicine'],
    permissions: ['view_clinicalItems', 'edit_reports', 'approve_reports', 'assign_clinicalItems', 'supervise_residents']
  },
  admin: {
    id: 'demo-admin',
    name: 'Dr. Sarah Admin',
    email: 'admin@demo.com',
    role: 'admin',
    specialties: ['Radiology', 'Administration'],
    permissions: ['view_clinicalItems', 'edit_reports', 'approve_reports', 'assign_clinicalItems', 'supervise_residents', 'manage_workflows', 'manage_users', 'system_settings']
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // If not in context, provide standalone implementation for backward compatibility
    return useStandaloneAuth();
  }
  return context;
};

const useStandaloneAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is logged in from localStorage
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    // Mock login - simulate API call
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        // Simple validation
        if (!email || !password) {
          setLoading(false);
          reject(new Error('Email and password are required'));
          return;
        }

        // Check for demo users first
        const demoUser = Object.values(DEMO_USERS).find(u => u.email === email);
        if (demoUser && password === 'demo') {
          setUser(demoUser);
          localStorage.setItem('user', JSON.stringify(demoUser));
          setLoading(false);
          resolve();
          return;
        }

        // Create mock user based on email pattern
        let role: 'resident' | 'supervisor' | 'admin' = 'resident';
        if (email.includes('admin')) role = 'admin';
        else if (email.includes('supervisor')) role = 'supervisor';

        const mockUser: User = {
          id: `user-${Date.now()}`,
          name: email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          email: email,
          role: role,
          specialties: role === 'admin' ? ['Administration'] : ['Radiology'],
          permissions: DEMO_USERS[role].permissions
        };

        setUser(mockUser);
        localStorage.setItem('user', JSON.stringify(mockUser));
        setLoading(false);
        resolve();
      }, 1000);
    });
  }, []);

  const signup = useCallback(async (userData: SignupData) => {
    setLoading(true);
    // Mock signup - simulate API call
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        if (!userData.name || !userData.email || !userData.password) {
          setLoading(false);
          reject(new Error('All fields are required'));
          return;
        }

        const newUser: User = {
          id: `user-${Date.now()}`,
          name: userData.name,
          email: userData.email,
          role: userData.role,
          specialties: userData.specialties || [userData.role === 'admin' ? 'Administration' : 'Radiology'],
          permissions: DEMO_USERS[userData.role].permissions
        };

        setUser(newUser);
        localStorage.setItem('user', JSON.stringify(newUser));
        setLoading(false);
        resolve();
      }, 1000);
    });
  }, []);

  const loginAsDemo = useCallback(async (role: 'resident' | 'supervisor' | 'admin') => {
    setLoading(true);
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        const demoUser = DEMO_USERS[role];
        setUser(demoUser);
        localStorage.setItem('user', JSON.stringify(demoUser));
        setLoading(false);
        resolve();
      }, 500);
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  }, [navigate]);

  return {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    signup,
    loginAsDemo,
    logout
  };
};

export { AuthContext, DEMO_USERS };
